import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Lock, LogOut, Users, Search, Download, RefreshCw, FileSpreadsheet, ArrowUpDown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD || 'tago2026';

const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [waitlistData, setWaitlistData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');
  const [sortField, setSortField] = useState('timestamp');
  const [sortAsc, setSortAsc] = useState(false);

  useEffect(() => {
    if (localStorage.getItem('tago_admin_auth') === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  const fetchWaitlistData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/waitlist`);
      const sortedData = response.data.sort((a, b) =>
        new Date(b.timestamp) - new Date(a.timestamp)
      );
      setWaitlistData(sortedData);
      setFilteredData(sortedData);
    } catch (err) {
      console.error('Error fetching waitlist:', err);
      setError('Failed to load waitlist data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) fetchWaitlistData();
  }, [isAuthenticated, fetchWaitlistData]);

  useEffect(() => {
    let data = [...waitlistData];
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      data = data.filter(e =>
        e.name?.toLowerCase().includes(term) ||
        e.email?.toLowerCase().includes(term) ||
        e.phone?.includes(searchTerm) ||
        e.city?.toLowerCase().includes(term)
      );
    }
    data.sort((a, b) => {
      const valA = a[sortField] || '';
      const valB = b[sortField] || '';
      if (sortField === 'timestamp') {
        return sortAsc ? new Date(valA) - new Date(valB) : new Date(valB) - new Date(valA);
      }
      return sortAsc ? String(valA).localeCompare(String(valB)) : String(valB).localeCompare(String(valA));
    });
    setFilteredData(data);
  }, [searchTerm, waitlistData, sortField, sortAsc]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      localStorage.setItem('tago_admin_auth', 'true');
      setError('');
    } else {
      setError('Incorrect password. Please try again.');
      setPassword('');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('tago_admin_auth');
    setPassword('');
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      setSortAsc(true);
    }
  };

  const downloadCSV = () => {
    const headers = ['Timestamp', 'Name', 'Email', 'Phone', 'Country Code', 'City', 'User Type', 'Source', 'Device'];
    const rows = filteredData.map(entry => [
      new Date(entry.timestamp).toLocaleString(),
      entry.name || '',
      entry.email || '',
      entry.phone || '',
      entry.country_code || '',
      entry.city || '',
      entry.user_type || '',
      entry.source || '',
      entry.device || ''
    ]);
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `tago_waitlist_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  if (!isAuthenticated) {
    return (
      <div data-testid="admin-login-screen" className="min-h-screen bg-[#040F1E] flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <h1 className="font-syne text-5xl font-extrabold text-[#00C4B4] mb-2" style={{ letterSpacing: '-1.5px' }}>
              TAGO
            </h1>
            <p className="text-white/70 font-dm text-lg">Admin Dashboard</p>
          </div>
          <div className="bg-[#0C2040] border border-[rgba(0,196,180,.2)] rounded-2xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#00C4B4]/10 rounded-full flex items-center justify-center">
                <Lock className="w-5 h-5 text-[#00C4B4]" />
              </div>
              <div>
                <h2 className="font-syne text-xl font-bold text-white">Secure Login</h2>
                <p className="text-white/50 text-sm font-dm">Enter admin password</p>
              </div>
            </div>
            <form onSubmit={handleLogin}>
              <div className="mb-4">
                <label className="block text-sm font-dm font-semibold text-white/70 mb-2">Password</label>
                <input
                  data-testid="admin-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter admin password"
                  className="w-full bg-[#040F1E] border border-[rgba(0,196,180,.2)] rounded-xl px-4 py-3 text-white font-dm focus:outline-none focus:border-[#00C4B4] transition-colors"
                  required
                  autoFocus
                />
                {error && <p data-testid="admin-login-error" className="mt-2 text-sm text-red-400 font-dm">{error}</p>}
              </div>
              <button
                data-testid="admin-login-button"
                type="submit"
                className="w-full bg-[#00C4B4] hover:bg-[#00DDD0] text-[#040F1E] font-dm font-bold py-3 px-6 rounded-xl transition-all hover:shadow-lg hover:shadow-[#00C4B4]/30"
              >
                Login to Dashboard
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#040F1E] text-white font-dm">
      {/* Header */}
      <div className="bg-[#0C2040] border-b border-[rgba(0,196,180,.2)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h1 className="font-syne text-3xl font-extrabold text-[#00C4B4]" style={{ letterSpacing: '-1.5px' }}>TAGO</h1>
              <span className="hidden sm:block text-white/30">|</span>
              <span className="text-white/70 font-semibold">Admin Dashboard</span>
            </div>
            <button
              data-testid="admin-logout-button"
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 bg-[#040F1E] hover:bg-[#081828] border border-[rgba(0,196,180,.2)] rounded-lg text-sm font-semibold text-white/70 hover:text-white transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <div data-testid="stat-total-signups" className="bg-[#0C2040] border border-[rgba(0,196,180,.2)] rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <Users className="w-6 h-6 text-[#00C4B4]" />
              <span className="text-sm text-white/50 font-semibold">Total Signups</span>
            </div>
            <div className="font-syne text-4xl font-extrabold text-[#00C4B4]">{waitlistData.length}</div>
          </div>
          <div data-testid="stat-filtered-results" className="bg-[#0C2040] border border-[rgba(0,196,180,.2)] rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <Search className="w-6 h-6 text-[#FF6B35]" />
              <span className="text-sm text-white/50 font-semibold">Filtered Results</span>
            </div>
            <div className="font-syne text-4xl font-extrabold text-[#FF6B35]">{filteredData.length}</div>
          </div>
          <div data-testid="stat-last-updated" className="bg-[#0C2040] border border-[rgba(0,196,180,.2)] rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <FileSpreadsheet className="w-6 h-6 text-[#22C55E]" />
              <span className="text-sm text-white/50 font-semibold">Last Updated</span>
            </div>
            <div className="font-dm text-lg font-semibold text-white">{new Date().toLocaleTimeString()}</div>
          </div>
        </div>

        {/* Controls */}
        <div className="bg-[#0C2040] border border-[rgba(0,196,180,.2)] rounded-2xl p-6 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
              <input
                data-testid="admin-search-input"
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name, email, phone, or city..."
                className="w-full bg-[#040F1E] border border-[rgba(0,196,180,.2)] rounded-xl pl-11 pr-4 py-3 text-white font-dm focus:outline-none focus:border-[#00C4B4] transition-colors"
              />
            </div>
            <div className="flex gap-2">
              <button
                data-testid="download-csv-button"
                onClick={downloadCSV}
                disabled={filteredData.length === 0}
                className="flex items-center gap-2 px-6 py-3 bg-[#00C4B4] hover:bg-[#00DDD0] text-[#040F1E] font-bold rounded-xl transition-all hover:shadow-lg disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
              >
                <Download className="w-4 h-4" />
                Download CSV
              </button>
              <button
                data-testid="refresh-data-button"
                onClick={fetchWaitlistData}
                className="flex items-center gap-2 px-4 py-3 bg-[#040F1E] hover:bg-[#081828] border border-[rgba(0,196,180,.2)] text-white/70 hover:text-white font-bold rounded-xl transition-all whitespace-nowrap"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div data-testid="loading-spinner" className="text-center py-20">
            <div className="inline-block w-12 h-12 border-4 border-[#00C4B4] border-t-transparent rounded-full animate-spin"></div>
            <p className="mt-4 text-white/50">Loading waitlist data...</p>
          </div>
        ) : filteredData.length === 0 ? (
          <div data-testid="empty-state" className="bg-[#0C2040] border border-[rgba(0,196,180,.2)] rounded-2xl p-12 text-center">
            <Users className="w-16 h-16 text-white/20 mx-auto mb-4" />
            <p className="text-xl text-white/70 font-semibold">No signups found</p>
            <p className="text-sm text-white/50 mt-2">
              {searchTerm ? 'Try a different search term' : 'Waiting for first signup...'}
            </p>
          </div>
        ) : (
          <div data-testid="waitlist-table-wrapper" className="bg-[#0C2040] border border-[rgba(0,196,180,.2)] rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table data-testid="waitlist-table" className="w-full">
                <thead>
                  <tr className="bg-[#040F1E] border-b border-[rgba(0,196,180,.2)]">
                    {[
                      { key: 'timestamp', label: 'Timestamp' },
                      { key: 'name', label: 'Name' },
                      { key: 'email', label: 'Email' },
                      { key: 'phone', label: 'Phone' },
                      { key: 'city', label: 'City' },
                      { key: 'user_type', label: 'User Type' },
                      { key: 'source', label: 'Source' },
                    ].map(col => (
                      <th
                        key={col.key}
                        onClick={() => handleSort(col.key)}
                        className="px-6 py-4 text-left text-xs font-bold text-[#00C4B4] uppercase tracking-wider cursor-pointer hover:text-[#00DDD0] select-none"
                      >
                        <span className="flex items-center gap-1">
                          {col.label}
                          <ArrowUpDown className="w-3 h-3 opacity-50" />
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[rgba(0,196,180,.1)]">
                  {filteredData.map((entry, index) => (
                    <tr key={entry.id || index} data-testid={`waitlist-row-${index}`} className="hover:bg-[#081828] transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-white/70">{formatDate(entry.timestamp)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-white">{entry.name || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-white/70">{entry.email || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-white/70">{entry.country_code} {entry.phone || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-white/70">{entry.city || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className="px-2 py-1 bg-[#00C4B4]/10 border border-[#00C4B4]/20 rounded-full text-xs text-[#00C4B4] font-semibold">
                          {entry.user_type || '-'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-white/70">{entry.source || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="bg-[#040F1E] px-6 py-4 border-t border-[rgba(0,196,180,.2)]">
              <p data-testid="table-footer-count" className="text-sm text-white/50">
                Showing <span className="text-[#00C4B4] font-semibold">{filteredData.length}</span> of <span className="text-[#00C4B4] font-semibold">{waitlistData.length}</span> entries
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
